/**
 * Product Form Handler
 * 
 * Handles variant selection, button state, and price updates for product forms
 */
(function() {
  'use strict';

  class ProductFormHandler {
    constructor(form) {
      this.form = form;
      // Try to find variant ID input with data-variant-id first, then fall back to regular name="id" input
      this.variantIdInput = form.querySelector('[data-variant-id]') || form.querySelector('input[name="id"]');
      this.addToCartButton = form.querySelector('.product-add-to-cart');
      this.buttonTextSpan = this.addToCartButton?.querySelector('span:first-child');
      this.priceSpan = this.addToCartButton?.querySelector('[data-price]');
      this.sizeButtons = form.querySelectorAll('[data-size]');
      
      // Get product data from data attributes
      this.productData = this.getProductData();
      
      this.init();
    }

    getProductData() {
      const scriptTag = document.querySelector('[data-product-json]');
      if (!scriptTag) {
        console.warn('ProductFormHandler: Missing product data');
        return null;
      }
      
      try {
        return JSON.parse(scriptTag.textContent);
      } catch (e) {
        console.error('ProductFormHandler: Failed to parse product data', e);
        return null;
      }
    }

    init() {
      if (!this.productData) {
        console.warn('ProductFormHandler: No product data available');
        return;
      }
      
      // Set up size button event listeners
      this.sizeButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleSizeSelection(button);
        });
      });

      // Set up form submission handler
      this.form.addEventListener('submit', (e) => {
        this.handleFormSubmit(e);
      });

      // Set initial state based on selected variant (this will check availability)
      this.updateFormState();
      
      // If product has only default variant and updateFormState didn't set it, check availability
      if (this.productData.has_only_default_variant && !this.addToCartButton?.classList.contains('sold-out') && this.addToCartButton?.disabled) {
        const defaultVariant = this.productData.variants[0];
        if (defaultVariant) {
          if (defaultVariant.available) {
            this.enableAddToCartButton();
          } else {
            this.setSoldOutState();
          }
        }
      }
    }

    handleFormSubmit(e) {
      // Prevent default form submission
      e.preventDefault();

      // Get the selected variant ID
      const variantId = this.variantIdInput?.value;
      if (!variantId) {
        console.warn('ProductFormHandler: No variant selected');
        return;
      }

      // Add to cart via AJAX
      this.addToCart(variantId);
    }

    async addToCart(variantId) {
      // Show loading state
      this.setLoadingState(true);

      // Ensure variantId is a number (Shopify API requires numeric ID)
      const numericVariantId = parseInt(variantId, 10);
      if (!numericVariantId || isNaN(numericVariantId)) {
        this.setLoadingState(false);
        console.error('Invalid variant ID:', variantId);
        return;
      }

      const formData = {
        items: [{
          id: numericVariantId,
          quantity: 1
        }],
        sections: 'cart-drawer-content'
      };

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          // Handle error response - check if product is sold out
          const errorData = await response.json().catch(() => ({}));
          
          // If product is sold out, update button state
          if (errorData.status === 422 && errorData.description) {
            // Check if error mentions sold out
            if (errorData.description.toLowerCase().includes('sold out')) {
              this.setSoldOutState();
              // Optionally show error message to user
              console.warn('Product is sold out:', errorData.description);
              return; // Exit early, don't proceed with cart update
            }
          }
          
          throw new Error(errorData.description || 'Failed to add to cart');
        }

        const responseData = await response.json();
        
        // Fetch full cart object to get item_count
        const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await cartRes.json();

        // Update cart drawer content using bundled section rendering
        if (responseData.sections && responseData.sections['cart-drawer-content']) {
          // Use the existing cart drawer update function if available
          if (typeof updateCartDrawerContent === 'function') {
            await updateCartDrawerContent(responseData.sections['cart-drawer-content'], cart);
          } else {
            // Fallback: manual update
            const cartDrawerBody = document.getElementById('cart-drawer-body');
            if (cartDrawerBody) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(responseData.sections['cart-drawer-content'], 'text/html');
              const sectionWrapper = doc.querySelector('#cart-drawer-body');
              if (sectionWrapper) {
                cartDrawerBody.innerHTML = sectionWrapper.innerHTML;
              }
            }
            
            // Update cart count
            if (typeof updateCartCount === 'function') {
              updateCartCount(cart.item_count);
            }
          }
        } else {
          // Fallback: refresh cart drawer
          if (typeof refreshCartDrawer === 'function') {
            await refreshCartDrawer(cart);
          }
        }

        // Update cart count in header (ensure it's updated)
        if (typeof updateCartCount === 'function') {
          updateCartCount(cart.item_count);
        } else {
          this.updateCartCount();
        }

        // Reset loading state after success
        this.setLoadingState(false);

        // Open cart drawer only after successful add
        this.openCartDrawer();

        // Dispatch success event
        this.form.dispatchEvent(new CustomEvent('cart:add', {
          detail: { variantId, cart }
        }));
      } catch (error) {
        console.error('ProductFormHandler: Error adding to cart', error);
        // Reset loading state on error
        this.setLoadingState(false);
        // You could show an error message to the user here
      }
    }

    setLoadingState(isLoading) {
      if (!this.addToCartButton) return;
      
      if (isLoading) {
        this.addToCartButton.disabled = true;
        if (this.buttonTextSpan) {
          this.buttonTextSpan.textContent = 'Adding...';
        }
        this.addToCartButton.classList.add('loading');
      } else {
        // Don't reset button state if it's sold out
        if (this.addToCartButton.classList.contains('sold-out')) {
          this.addToCartButton.classList.remove('loading');
          return; // Keep sold out state
        }
        
        this.addToCartButton.disabled = false;
        if (this.buttonTextSpan) {
          this.buttonTextSpan.textContent = 'Add to cart';
        }
        this.addToCartButton.classList.remove('loading');
      }
    }

    async updateCartCount() {
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await response.json();
        
        // Use the global updateCartCount function if available
        if (typeof updateCartCount === 'function') {
          updateCartCount(cart.item_count);
        } else {
          // Fallback: manual update
          const cartCountElements = document.querySelectorAll('.header__icons span, .header__mobile-cart span');
          cartCountElements.forEach(el => {
            if (el) el.textContent = cart.item_count;
          });
        }
      } catch (error) {
        console.error('ProductFormHandler: Error updating cart count', error);
      }
    }

    openCartDrawer() {
      const cartDrawer = document.querySelector('[data-cart-drawer]');
      if (cartDrawer) {
        cartDrawer.open();
        
        // Switch to cart tab
        const tabs = document.querySelector('cart-tabs');
        if (tabs) tabs.switchTab('cart');
      }
    }

    handleSizeSelection(selectedButton) {
      const selectedSize = selectedButton.getAttribute('data-size');
      
      // Update visual state of buttons
      this.sizeButtons.forEach(button => {
        button.classList.remove('selected');
      });
      selectedButton.classList.add('selected');

      // Find matching variant
      const variant = this.findVariantBySize(selectedSize);
      
      if (variant) {
        this.selectVariant(variant);
      } else {
        console.warn('ProductFormHandler: No variant found for size:', selectedSize);
        // If no variant found, disable button
        this.disableAddToCartButton();
      }
    }

    findVariantBySize(size) {
      if (!this.productData || !this.productData.variants) return null;
      
      // Find variant that matches the selected size
      return this.productData.variants.find(variant => {
        return variant.options && variant.options.includes(size);
      });
    }

    selectVariant(variant) {
      // Update hidden variant ID input
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
      }

      // Update price display if priceSpan exists
      if (this.priceSpan && variant.price) {
        const formattedPrice = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
          minimumFractionDigits: 2
        }).format(variant.price / 100);
        this.priceSpan.textContent = formattedPrice;
      }

      // Check availability and update button state
      // Check for false, 0, null, or undefined
      if (variant.available === false || variant.available === 0 || !variant.available) {
        console.log('ProductFormHandler: Variant is sold out:', variant.id);
        this.setSoldOutState();
      } else {
        console.log('ProductFormHandler: Variant is available:', variant.id);
        this.enableAddToCartButton();
      }

      // Dispatch custom event for other scripts that might need to react
      this.form.dispatchEvent(new CustomEvent('variant:change', {
        detail: { variant }
      }));
    }

    enableAddToCartButton() {
      if (!this.addToCartButton) return;
      
      this.addToCartButton.disabled = false;
      this.addToCartButton.classList.remove('sold-out');
      
      if (this.buttonTextSpan) {
        this.buttonTextSpan.textContent = 'Add to cart';
      }
    }

    setSoldOutState() {
      if (!this.addToCartButton) {
        console.warn('ProductFormHandler: Add to cart button not found');
        return;
      }
      
      this.addToCartButton.disabled = true;
      this.addToCartButton.classList.add('sold-out');
      
      // Update button text - try multiple ways to find the text span
      if (this.buttonTextSpan) {
        this.buttonTextSpan.textContent = 'Sold out';
      } else {
        // Fallback: find the first span in the button
        const firstSpan = this.addToCartButton.querySelector('span:first-child');
        if (firstSpan) {
          firstSpan.textContent = 'Sold out';
        } else {
          // Last resort: update button textContent directly
          const priceSpan = this.addToCartButton.querySelector('[data-price]');
          if (priceSpan) {
            this.addToCartButton.innerHTML = '<span>Sold out</span>' + priceSpan.outerHTML;
          } else {
            this.addToCartButton.textContent = 'Sold out';
          }
        }
      }
      
      console.log('ProductFormHandler: Set sold out state');
    }

    disableAddToCartButton() {
      if (!this.addToCartButton) return;
      
      this.addToCartButton.disabled = true;
      this.addToCartButton.classList.remove('sold-out');
      
      if (this.buttonTextSpan) {
        this.buttonTextSpan.textContent = 'Select a size';
      }
    }

    updateFormState() {
      // Check if a variant is already selected (from URL parameter or default)
      const currentVariantId = this.variantIdInput?.value;
      
      if (!currentVariantId || !this.productData?.variants) {
        // No variant selected yet
        // For single-variant products, check the first variant's availability
        if (this.productData?.has_only_default_variant && this.productData.variants.length > 0) {
          const defaultVariant = this.productData.variants[0];
          if (defaultVariant.available === false || defaultVariant.available === 0 || !defaultVariant.available) {
            console.log('ProductFormHandler: Default variant is sold out, setting sold out state');
            this.setSoldOutState();
          } else {
            console.log('ProductFormHandler: Default variant is available, enabling button');
            this.enableAddToCartButton();
          }
        }
        return;
      }
      
      const currentVariant = this.productData.variants.find(
        v => v.id.toString() === currentVariantId.toString()
      );
      
      if (!currentVariant) {
        console.warn('ProductFormHandler: Variant not found:', currentVariantId);
        return;
      }
      
      // Debug logging
      console.log('ProductFormHandler: Current variant:', {
        id: currentVariant.id,
        available: currentVariant.available,
        options: currentVariant.options
      });
      
      // Find and select the corresponding size button
      // Check if Size option exists in options_with_values
      const sizeOptionGroup = this.productData.options_with_values?.find(
        optGroup => optGroup.name === 'Size'
      );
      
      if (sizeOptionGroup && currentVariant.options) {
        // Find the size option that matches one of the variant's options
        const sizeOption = currentVariant.options.find(opt => 
          sizeOptionGroup.values.includes(opt)
        );
        
        if (sizeOption) {
          const matchingButton = Array.from(this.sizeButtons).find(btn => 
            btn.getAttribute('data-size') === sizeOption
          );
          
          if (matchingButton) {
            matchingButton.classList.add('selected');
          }
        }
      }
      
      // Always check availability and update button state
      // This handles both products with and without size selectors
      // Check for false, 0, null, or undefined
      if (currentVariant.available === false || currentVariant.available === 0 || !currentVariant.available) {
        console.log('ProductFormHandler: Variant is sold out, setting sold out state');
        this.setSoldOutState();
      } else {
        console.log('ProductFormHandler: Variant is available, enabling button');
        this.enableAddToCartButton();
      }
    }

  }

  // Initialize when DOM is ready
  function initProductForms() {
    const productForms = document.querySelectorAll('[data-product-form]');
    
    productForms.forEach(form => {
      new ProductFormHandler(form);
    });
  }

  // Initialize on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductForms);
  } else {
    initProductForms();
  }
})();

